import { useState } from "react";
import { Globe2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";

export default function DisplayLanguageSelector({ compact = false }) {
  const { language, languages, setDisplayLanguage } = useLanguage();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const handleChange = async (code) => {
    if (code === language || saving) return;
    setSaving(true);
    const ok = await setDisplayLanguage(code);
    setSaving(false);
    if (ok) {
      toast.success(`${t("language_updated")}: ${code}`);
    } else {
      toast.error(t("language_update_failed"));
    }
  };

  const toggle = (
    <div
      data-testid="display-language-toggle"
      className={`inline-flex items-center rounded-md border border-border bg-card p-0.5 ${compact ? "h-8" : ""}`}
    >
      {languages.map((item, index) => (
        <span key={item.code} className="inline-flex items-center">
          <button
            type="button"
            onClick={() => handleChange(item.code)}
            disabled={saving}
            className={`h-7 px-2 text-xs font-medium transition-colors ${
              language === item.code
                ? "rounded bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            aria-pressed={language === item.code}
          >
            {item.code}
          </button>
          {index < languages.length - 1 && (
            <span className="px-0.5 text-xs text-border" aria-hidden="true">
              |
            </span>
          )}
        </span>
      ))}
    </div>
  );

  if (compact) return toggle;

  return (
    <div data-testid="display-language-section" className="space-y-2">
      <div className="flex items-center gap-2">
        <Globe2 className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-semibold">{t("display_language")}</p>
      </div>
      <p className="text-xs text-muted-foreground">{t("display_language_help")}</p>
      {toggle}
    </div>
  );
}
